import type { ColorwayOption } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { swatchColorsByColorway } from '@apps/card/utils/colorway';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React from 'react';

type Props = Readonly<{
  options: ReadonlyArray<ColorwayOption>;
  selectedId: string;
  onSelect: (id: string) => void;
  size?: SwatchSize;
  /**
   * 'wrap' (default): render every swatch, wrapping to the next line.
   * 'overflow': clamp to a responsive max (see the useResponsive call) and
   * append a "+N" remaining-count label that fires `onOverflowClick`.
   */
  variant?: 'wrap' | 'overflow';
  /** Receives the number of colorways the "+N" label stands in for. */
  onOverflowClick?: (hiddenCount: number) => void;
  /** Show a tooltip on each swatch with the capitalized colorwayId. Hidden by default. */
  showTooltip?: boolean;
}>;

// Ring geometry mirrors the Foil swatch (FoilChoiceAttribute): the outer button's
// background *is* the ring, sitting RING_INSET outside the color disc.
const RING_INSET = 3;
const DISC_SIZE = { small: 20, large: 38 } as const;

type SwatchSize = keyof typeof DISC_SIZE;

/**
 * Splits the disc into N equal diagonal bands (45deg, like a slash) — one per
 * colorway color. Hard stops (each color ending where the next begins) give
 * crisp edges rather than a blend. A single color yields two identical stops,
 * i.e. a flat fill.
 */
const diagonalBands = (colors: ReadonlyArray<string>) => {
  const stops = colors.flatMap((color, index) => {
    const from = (index / colors.length) * 100;
    const to = ((index + 1) / colors.length) * 100;
    return [`${color} ${from}%`, `${color} ${to}%`];
  });
  return `linear-gradient(135deg, ${stops.join(', ')})`;
};

export const CardColorSwatches = ({ options, selectedId, onSelect, size = 'small', variant = 'wrap', onOverflowClick, showTooltip = false }: Props) => {
  const { tq } = useTranslation('stationery');
  const discSize = DISC_SIZE[size];
  const outerSize = discSize + RING_INSET * 2;
  // `large` keeps the foil row's gap={3} (8px) for exact parity on the PDP. On the
  // compact gallery tiles the ring already adds RING_INSET on both sides, so the
  // full 8px would read as 14px between discs — subtract the ring back out.
  const gap = size === 'large' ? 3 : '2px';
  // Max swatches before collapsing into the overflow chip; grows where there is
  // room, stepping at the same breakpoints the gallery grid uses (md3 >= 1280px
  // → 4 columns, xl >= 1920px → 6 columns). initialValue keeps SSR/first-paint
  // at the narrow count.
  const [maxVisible] = useResponsive({ values: { mobile: 4, md3: 6, xl: 8 } }, 4);
  // Which colors each swatch shows depends on the whole set (see
  // swatchColorsByColorway), so it is resolved once here rather than per swatch.
  // Computed over every option, not just the visible ones, so the overflow chip
  // can't change what the visible swatches show.
  const colorsById = React.useMemo(() => swatchColorsByColorway(options), [options]);
  // Collapse into the "+N" label only when it stands in for 2+ hidden colors. A
  // single hidden colorway would show as "+1", which is barely worth a click
  // (and the click navigates to the PDP) — so in that case show them all.
  const isClamped = variant === 'overflow' && !!onOverflowClick && options.length > maxVisible + 1;
  const visibleOptions = isClamped ? options.slice(0, maxVisible) : options;
  const hiddenCount = isClamped ? options.length - maxVisible : 0;
  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      gap={gap}
      flexWrap={isClamped ? undefined : 'wrap'}
      onClick={e => e.stopPropagation()}
    >
      {visibleOptions.map(({ id }) => {
        const colors = colorsById.get(id) ?? ['transparent'];
        const isSelected = id === selectedId;
        // White separator between disc and ring, matching the foil swatch's
        // 1px-selected / 2px-unselected outline. Rendered as a box-shadow rather
        // than an `outline` so it follows the border radius. The inset hairline
        // keeps white/ivory colorways distinguishable from the light mono2 ring.
        const separator = isSelected ? 1 : 2;
        const swatch = (
          <Flex
            key={id}
            as="button"
            type="button"
            aria-label={id}
            aria-pressed={isSelected}
            onClick={() => onSelect(id)}
            width={`${outerSize}px`}
            height={`${outerSize}px`}
            borderRadius="50%"
            justifyContent="center"
            alignItems="center"
            position="relative"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            backgroundColor={isSelected ? 'mono14' : 'mono2'}
          >
            <Box
              position="absolute"
              width={`${discSize}px`}
              height={`${discSize}px`}
              borderRadius="50%"
              pointerEvents="none"
              style={{
                backgroundImage: diagonalBands(colors),
                boxShadow: `0 0 0 ${separator}px white, 0 0 0 ${separator}px rgba(0,0,0,0.12) inset`,
              }}
            />
          </Flex>
        );
        return showTooltip ? (
          <TooltipV2
            key={id}
            content={<TextV2 textTransform="capitalize">{id}</TextV2>}
            placement="top"
            onMouseLeaveDelay={0}
          >
            {swatch}
          </TooltipV2>
        ) : (
          swatch
        );
      })}
      {isClamped && (
        <TooltipV2
          content={<TextV2>{tq`View all colors`}</TextV2>}
          placement="top"
          onMouseLeaveDelay={0}
        >
          <TextV2
            as="button"
            type="button"
            aria-label={tq`View all colors`}
            onClick={() => onOverflowClick?.(hiddenCount)}
            typographyVariant="label2"
            color="mono12"
            whiteSpace="nowrap"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            // Text has no ring to sit inside, so top up the tightened swatch gap.
            marginLeft="3px"
            backgroundColor="transparent"
          >
            +{hiddenCount}
          </TextV2>
        </TooltipV2>
      )}
    </Flex>
  );
};
